/**
 * Canonical note reader URLs.
 *
 * INVARIANT (do not regress): opening a note MUST navigate here so the
 * address bar shows a unique URL. Never open TextReader as a modal overlay
 * on `/`, `/dashboard`, etc. — that leaves the home URL in the bar.
 *
 * Prefer a stable text ID under `/notas/id/:id`. Titles, slugs and folders are
 * editable metadata and must never invalidate a shared article URL.
 * Legacy date/slug and `/a/` routes remain readable for old links.
 */
export const HOME_PATH = "/";

export function textReaderPath(text) {
  if (!text) return HOME_PATH;
  const id = text.id || text.textId;
  const slug = text.slug || id;
  if (id) return `/notas/id/${id}`;
  if (slug && text.dateSlug) {
    return `/notas/${text.dateSlug}/${slug}`;
  }
  if (text.deskSlug && text.drawerSlug && text.notebookSlug && slug) {
    return `/a/${text.deskSlug}/${text.drawerSlug}/${text.notebookSlug}/${slug}?spa=true`;
  }
  if (slug) return `/a/${slug}?spa=true`;
  return HOME_PATH;
}

/** Navigate to the note's unique URL. Use this instead of setReader / modal open. */
export function navigateToText(navigate, text, navOptions) {
  const path =
    (text?.url && text.url !== HOME_PATH && text.url) ||
    textReaderPath(text);
  if (!path || path === HOME_PATH) return false;
  navigate(path, navOptions);
  return true;
}

function textsAsArray(notebook) {
  if (!notebook?.texts) return [];
  if (Array.isArray(notebook.texts)) return notebook.texts;
  return Object.entries(notebook.texts).map(([id, t]) => ({ ...t, id }));
}

/** Resolve a saved note from the live workspace tree (avoids stale cache / race). */
export function findTextInWorkspace(workspace, { deskId, drawerId, notebookId, textId, deskSlug, drawerSlug, notebookSlug, slug } = {}) {
  if (!workspace?.desks?.length) return null;
  const matchSlug = slug || textId;

  for (const desk of workspace.desks) {
    if (deskId && desk.id !== deskId) continue;
    if (deskSlug && desk.slug && desk.slug !== deskSlug) continue;
    const drawers = desk.drawers ? (Array.isArray(desk.drawers) ? desk.drawers : Object.values(desk.drawers)) : [];
    for (const drawer of drawers) {
      if (drawerId && drawer.id !== drawerId) continue;
      if (drawerSlug && drawer.slug && drawer.slug !== drawerSlug) continue;
      const notebooks = drawer.notebooks ? (Array.isArray(drawer.notebooks) ? drawer.notebooks : Object.values(drawer.notebooks)) : [];
      for (const nb of notebooks) {
        if (notebookId && nb.id !== notebookId) continue;
        if (notebookSlug && nb.slug && nb.slug !== notebookSlug) continue;
        const texts = textsAsArray(nb);
        for (const t of texts) {
          if (t.id === textId || (matchSlug && t.slug === matchSlug)) {
            return {
              ...t,
              deskId: desk.id,
              drawerId: drawer.id,
              notebookId: nb.id,
              deskName: desk.name,
              drawerName: drawer.name,
              notebookName: nb.name,
              deskSlug: desk.slug,
              drawerSlug: drawer.slug,
              notebookSlug: nb.slug,
            };
          }
        }
      }
    }
  }
  return null;
}

export function categoryPathLabel(path, workspace, toSortedArray) {
  if (!path) return "";
  if (path.deskName) {
    return `${path.deskName} › ${path.drawerName || "?"} › ${path.notebookName || "?"}`;
  }
  if (path.deskId && workspace?.desks && toSortedArray) {
    const desk = workspace.desks.find((d) => d.id === path.deskId);
    const drawer = desk && toSortedArray(desk.drawers).find((d) => d.id === path.drawerId);
    const nb = drawer && toSortedArray(drawer.notebooks).find((n) => n.id === path.notebookId);
    if (desk && drawer && nb) return `${desk.name} › ${drawer.name} › ${nb.name}`;
  }
  return "Carpeta asignada";
}
